import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { currencyService } from '../services/currencyService';
import { paymentSelectionService } from '../services/paymentSelectionService';
import { getAllServices, getServiceById, type Service } from '../services/servicesStore';
import {
  getSelectedServiceOptions,
  getServiceBillingLabel,
  getServiceOptionKey,
  getServiceOptionsSummary,
  getServicePricePrefix,
  getServiceTotalPrice,
  hasStartingPrice,
  isSingleChoiceService,
} from '../services/serviceCatalogUtils';
import { absoluteUrl } from '../lib/seo';

const Purchase = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [, setDisplayCountry] = useState(currencyService.getCountry());
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState<'full' | 'klarna'>('full');
  const [selectedOptionKeys, setSelectedOptionKeys] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'description' | 'features' | 'technical' | 'benefits'>('description');

  useEffect(() => {
    const existing = getServiceById(serviceId || '');
    if (existing) {
      setService(existing);
      setLoading(false);
    } else {
      getAllServices().then((data) => {
        const safeData = data || [];
        const foundService = safeData.find((s: any) => s.id === serviceId);
        setService(foundService || null);
        setLoading(false);
      });
    }
  }, [serviceId]);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
    });
  }, []);

  useEffect(() => {
    if (!service?.id) {
      return;
    }

    const selection = paymentSelectionService.getCheckoutSelection(service.id);
    setSelectedPayment(selection.paymentType);
    setSelectedOptionKeys(selection.selectedOptionKeys || []);
  }, [service?.id]);

  const selectedOptions = service
    ? getSelectedServiceOptions(service, selectedOptionKeys)
    : [];
  const totalPrice = service ? getServiceTotalPrice(service, selectedOptionKeys) : 0;
  const usesSingleChoiceOptions = service ? isSingleChoiceService(service) : false;
  const selectedPackLabel = usesSingleChoiceOptions ? selectedOptions[0]?.label ?? '' : '';

  useEffect(() => {
    if (!service?.id || !usesSingleChoiceOptions || !service.options?.length || selectedOptionKeys.length > 0) {
      return;
    }

    setSelectedOptionKeys([getServiceOptionKey(service.id, 0)]);
  }, [selectedOptionKeys.length, service?.id, service?.options, usesSingleChoiceOptions]);

  const isStartingPrice = hasStartingPrice(service ?? { pricingMode: 'fixed', options: [] });
  const showKlarna = !isStartingPrice && currencyService.canUseKlarna(
    totalPrice,
    service?.billingType ?? 'one-time'
  );
  const supportsKlarna = currencyService.supportsKlarna();
  const klarnaInstallmentAmount = currencyService.getKlarnaInstallmentAmount(totalPrice);
  const exceedsKlarnaLimit =
    supportsKlarna &&
    service?.billingType === 'one-time' &&
    Number(totalPrice ?? 0) > currencyService.getKlarnaMaxAmountEUR();

  useEffect(() => {
    if (!showKlarna && selectedPayment === 'klarna') {
      setSelectedPayment('full');
    }
  }, [selectedPayment, showKlarna]);

  useEffect(() => {
    if (!service?.id) {
      return;
    }

    paymentSelectionService.saveSelection({
      paymentType: showKlarna && selectedPayment === 'klarna' ? 'klarna' : 'full',
      serviceId: service.id,
      selectedOptionKeys,
    });
  }, [selectedOptionKeys, selectedPayment, service?.id, showKlarna]);

  const handlePurchase = () => {
    if (!service?.id) {
      return;
    }

    paymentSelectionService.saveSelection({
      paymentType: showKlarna && selectedPayment === 'klarna' ? 'klarna' : 'full',
      serviceId: service.id,
      selectedOptionKeys,
    });
    navigate(`/payment/${serviceId}`);
  };

  const getBillingTypeText = (billingType: string) => {
    switch (billingType) {
      case 'monthly':
        return 'Abonnement mensuel';
      case 'days':
        return `${service?.workDays ?? 0} jours de travail`;
      default: // one-time
        return 'Paiement unique';
    }
  };

  const formatPrice = (price: number) => currencyService.formatPrice(price);

  const handleToggleOption = (optionIndex: number) => {
    if (!service?.id) {
      return;
    }

    const optionKey = getServiceOptionKey(service.id, optionIndex);
    setSelectedOptionKeys((current) =>
      usesSingleChoiceOptions
        ? (current.includes(optionKey) ? current : [optionKey])
        : current.includes(optionKey)
          ? current.filter((entry) => entry !== optionKey)
          : [...current, optionKey]
    );
  };

  const getOptionPriceLabel = (option: NonNullable<Service['options']>[number]) => {
    const suffix = getOptionBillingText(option);

    if (option.pricingMode === 'replace-total') {
      return `${formatPrice(option.price)}${suffix}`;
    }

    return `+ ${formatPrice(option.price)}${suffix}`;
  };

  const getOptionBillingText = (option: NonNullable<Service['options']>[number]) => {
    if (option?.billingType === 'monthly') {
      return '/mois';
    }

    if (option?.billingType === 'days') {
      return '/jour';
    }

    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-neon-orange"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-bold mb-4 text-center">Service non trouvé</h1>
        <p className="text-gray-600 mb-8 text-center">
          Le service que vous recherchez n'existe pas ou n'est plus disponible.
        </p>
        <Link
          to="/services"
          className="bg-neon-orange text-white px-6 py-3 rounded-full font-semibold hover:bg-[#FF4500] transition-colors duration-300"
        >
          Voir tous nos services
        </Link>
      </div>
    );
  }

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    category: service.category,
    provider: {
      '@type': 'Organization',
      name: 'Couteau Suisse Digital',
    },
    offers: {
      '@type': 'Offer',
      price: service.price,
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      url: absoluteUrl(`/acheter/${service.id}`),
    },
  };

  return (
    <>
      <Helmet>
        <title>{service.name} - Couteau Suisse Digital</title>
        <meta name="description" content={service.longDescription} />
        <meta name="keywords" content={`${service.category}, ${service.name}, services digitaux, transformation digitale`} />
        <meta property="og:title" content={`${service.name} - Couteau Suisse Digital`} />
        <meta property="og:description" content={service.description} />
        <meta property="og:type" content="product" />
        <link rel="canonical" href={absoluteUrl(`/acheter/${service.id}`)} />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Service Header */}
          <div className="text-center mb-8 md:mb-12">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block text-neon-orange font-medium text-xs md:text-base px-4 md:px-6 py-2 bg-neon-orange/10 rounded-full border border-neon-orange/20 mb-4 md:mb-6"
            >
              {service.category}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl md:text-4xl font-bold mb-3 md:mb-4"
            >
              {service.name}
            </motion.h1>
            <p className="text-base md:text-xl text-gray-600">{service.description}</p>
          </div>

          {/* Mobile Price Card - Shown at top on mobile */}
          <div className="md:hidden mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
            >
              <div className="text-center mb-6">
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
                  {getServicePricePrefix(service)}
                </div>
                <div className="mt-2 text-3xl font-bold text-neon-orange mb-2">
                  {formatPrice(totalPrice)}
                </div>
                <div className="text-sm text-gray-600">
                  {getServiceBillingLabel(service)}
                </div>
                {showKlarna ? (
                  <div className="text-sm text-gray-600">
                    Option 3x Klarna: {formatPrice(klarnaInstallmentAmount)} /mois
                  </div>
                ) : exceedsKlarnaLimit ? (
                  <div className="text-sm text-amber-700">
                    Klarna est reserve aux projets jusqu a {currencyService.getKlarnaMaxAmountEUR()} EUR.
                  </div>
                ) : isStartingPrice ? (
                  <div className="text-sm text-gray-500">
                    {usesSingleChoiceOptions
                      ? (selectedPackLabel ? `${selectedPackLabel} actif.` : 'Choisissez le pack adapte a votre campagne.')
                      : selectedOptions.length > 0
                        ? `${selectedOptions.length} option${selectedOptions.length > 1 ? 's' : ''} selectionnee${selectedOptions.length > 1 ? 's' : ''}.`
                        : `Configuration de depart. ${getServiceOptionsSummary(service) || 'Options sur demande'}.`}
                  </div>
                ) : null}
              </div>

              {isStartingPrice && (
                <div className="mb-5 rounded-2xl border border-neon-orange/20 bg-neon-orange/5 p-4 text-sm text-gray-700">
                  {usesSingleChoiceOptions
                    ? `Pack de depart: ${formatPrice(service.price)}. ${selectedPackLabel ? `Pack actif: ${selectedPackLabel}. Total actuel: ${formatPrice(totalPrice)}.` : 'Choisissez un pack 30 jours.'}`
                    : `Tarif de depart: ${formatPrice(service.price)}. ${selectedOptions.length > 0
                      ? `Total actuel avec options: ${formatPrice(totalPrice)}.`
                      : 'Ajoutez ici les options comme pages supplementaires, multi-langue, contenus ou integrations.'}`}
                </div>
              )}

              <div className="space-y-4">
                <button
                  onClick={handlePurchase}
                  className="w-full bg-gradient-to-r from-neon-orange to-[#FF4500] text-white py-3 rounded-full font-semibold relative overflow-hidden group"
                >
                  <span className="relative z-10">{isStartingPrice ? 'Configurer puis commander' : 'Acheter maintenant'}</span>
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 1 }}
                  />
                </button>
              </div>
            </motion.div>
          </div>

          {/* Mobile Tab Navigation */}
          <div className="md:hidden mb-6 overflow-x-auto">
            <div className="flex space-x-2 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${
                    activeTab === tab.id ? 'bg-neon-orange text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Left Column - Service Details */}
            <div className="space-y-8">
              {/* Description Section */}
              <div className={`space-y-6 ${activeTab === 'description' ? 'block' : 'hidden md:block'}`}>
                <h2 className="text-2xl font-bold">Description détaillée</h2>
                <p className="text-gray-600 whitespace-pre-line">{service.longDescription}</p>

                {isStartingPrice && (
                  <div className="rounded-2xl border border-neon-orange/20 bg-neon-orange/5 p-6">
                    <h3 className="mb-2 text-lg font-semibold text-gray-900">Logique tarifaire</h3>
                    <p className="text-gray-600">
                      Cette offre est construite comme une base claire et commandable. Les besoins
                      additionnels comme pages, langues, contenus, tracking, automatisations ou
                      integrations sont ajoutes selon votre perimetre reel.
                    </p>
                  </div>
                )}
              </div>

              {/* Features Section */}
              <div className={`space-y-6 ${activeTab === 'features' ? 'block' : 'hidden md:block'}`}>
                <h2 className="text-2xl font-bold">Fonctionnalités incluses</h2>
                <div className="space-y-4">
                  {service.features && service.features.map((feature: string, index: number) => (
                    <div key={index} className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-neon-orange flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Technical Details Section */}
              <div className={`space-y-6 ${activeTab === 'technical' ? 'block' : 'hidden md:block'}`}>
                <h2 className="text-2xl font-bold">Détails techniques</h2>
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-semibold mb-4">Technologies utilisées</h3>
                    <div className="flex flex-wrap gap-2">
                      {service.technicalDetails && service.technicalDetails.technologies &&
                        service.technicalDetails.technologies.map((tech: string, index: number) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-white rounded-full text-sm text-gray-600 shadow-sm"
                          >
                            {tech}
                          </span>
                        ))
                      }
                    </div>
                  </div>
                </div>
              </div>

              {service.options && service.options.length > 0 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">
                    {service.optionsTitle || (usesSingleChoiceOptions ? 'Choisissez votre pack' : 'Options fréquentes')}
                  </h2>
                  <div className="grid gap-4">
                    {service.options.map((option, index) => (
                      <div
                        key={`${service.id}-option-${index}`}
                        className={`rounded-2xl border p-5 shadow-sm transition-all duration-300 ${
                          selectedOptionKeys.includes(getServiceOptionKey(service.id, index))
                            ? 'border-neon-orange bg-neon-orange/5 shadow-md'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => handleToggleOption(index)}
                                className={`flex h-6 w-6 items-center justify-center rounded-full border-2 transition-all duration-300 ${
                                  selectedOptionKeys.includes(getServiceOptionKey(service.id, index))
                                    ? 'border-neon-orange bg-neon-orange text-white'
                                    : 'border-gray-300 bg-white text-transparent'
                                }`}
                                aria-pressed={selectedOptionKeys.includes(getServiceOptionKey(service.id, index))}
                                aria-label={`${selectedOptionKeys.includes(getServiceOptionKey(service.id, index)) ? 'Retirer' : 'Ajouter'} ${option.label}`}
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <h3 className="text-lg font-semibold text-gray-900">{option.label}</h3>
                            </div>
                            {option.description && (
                              <p className="mt-2 text-gray-600">{option.description}</p>
                            )}
                          </div>
                          <div className="text-left md:text-right">
                            <div className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-400">
                              {usesSingleChoiceOptions ? 'Pack' : 'Option'}
                            </div>
                            <div className="mt-1 text-xl font-bold text-neon-orange">
                              {getOptionPriceLabel(option)}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleToggleOption(index)}
                              className="mt-2 text-sm font-semibold text-neon-orange hover:text-[#FF4500]"
                            >
                              {selectedOptionKeys.includes(getServiceOptionKey(service.id, index))
                                ? (usesSingleChoiceOptions ? 'Retirer ce pack' : 'Retirer cette option')
                                : (usesSingleChoiceOptions ? 'Choisir ce pack' : 'Ajouter au total')}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Benefits Section */}
              <div className={`space-y-6 ${activeTab === 'benefits' ? 'block' : 'hidden md:block'}`}>
                <h2 className="text-2xl font-bold">Bénéfices</h2>
                <div className="space-y-4">
                  {service.benefits && service.benefits.map((benefit: string, index: number) => (
                    <div key={index} className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-neon-orange flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Purchase Card */}
            <div className="hidden md:block">
              <div className="sticky top-24">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-8 shadow-lg border border-gray-100"
                >
                  <div className="text-center mb-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
                      {getServicePricePrefix(service)}
                    </div>
                    <div className="mt-2 text-4xl font-bold text-neon-orange mb-2">
                      {formatPrice(totalPrice)}
                    </div>
                    <div className="text-gray-600">{getServiceBillingLabel(service)}</div>
                    {showKlarna ? (
                      <div className="text-gray-600">
                        Option 3x Klarna: {formatPrice(klarnaInstallmentAmount)} /mois
                      </div>
                    ) : exceedsKlarnaLimit ? (
                      <div className="text-amber-700">
                        Klarna est reserve aux projets jusqu a {currencyService.getKlarnaMaxAmountEUR()} EUR.
                      </div>
                    ) : isStartingPrice ? (
                      <div className="text-gray-500">
                        {usesSingleChoiceOptions
                          ? (selectedPackLabel || 'Choix du pack en cours')
                          : selectedOptions.length > 0
                            ? `${selectedOptions.length} option${selectedOptions.length > 1 ? 's' : ''} selectionnee${selectedOptions.length > 1 ? 's' : ''}`
                            : (getServiceOptionsSummary(service) || 'Options a ajouter selon le projet')}
                      </div>
                    ) : null}
                  </div>

                  {/* Payment Options */}
                  {isStartingPrice ? (
                    <div className="mb-8">
                      <div className="rounded-xl border-2 border-neon-orange bg-neon-orange/5 p-4">
                        <div className="text-lg font-semibold mb-1">
                          {usesSingleChoiceOptions ? 'Pack selectionne' : 'Configuration de depart'}
                        </div>
                        <div className="text-sm text-gray-600">
                          {usesSingleChoiceOptions
                            ? (selectedPackLabel || 'Choix du pack en cours')
                            : `Tarif de depart: ${formatPrice(service.price)}`}
                        </div>
                        {(selectedOptions.length > 0 || usesSingleChoiceOptions) && (
                          <div className="mt-2 text-sm font-semibold text-neon-orange">
                            {usesSingleChoiceOptions ? `Total du pack: ${formatPrice(totalPrice)}` : `Total avec options: ${formatPrice(totalPrice)}`}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : service.billingType === 'monthly' ? (
                    <div className="mb-8">
                      <div className="p-4 rounded-xl border-2 border-neon-orange bg-neon-orange/5">
                        <div className="text-lg font-semibold mb-1">Abonnement mensuel</div>
                        <div className="text-sm text-gray-600">{formatPrice(totalPrice)} /mois</div>
                      </div>
                    </div>
                  ) : service.billingType === 'days' ? (
                    <div className="mb-8">
                      <div className="p-4 rounded-xl border-2 border-neon-orange bg-neon-orange/5">
                        <div className="text-lg font-semibold mb-1">Paiement unique</div>
                        <div className="text-sm text-gray-600">{formatPrice(totalPrice)}</div>
                      </div>
                    </div>
                  ) : showKlarna ? (
                    <div className="space-y-4 mb-8">
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => setSelectedPayment('full')}
                          className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                            selectedPayment === 'full'
                              ? 'border-neon-orange bg-neon-orange/5'
                              : 'border-gray-200 hover:border-neon-orange/50'
                          }`}
                        >
                          <div className="text-lg font-semibold mb-1">Paiement complet</div>
                          <div className="text-sm text-gray-600">{formatPrice(totalPrice)}</div>
                        </button>
                        <button
                          onClick={() => setSelectedPayment('klarna')}
                          className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                            selectedPayment === 'klarna'
                              ? 'border-neon-orange bg-neon-orange/5'
                              : 'border-gray-200 hover:border-neon-orange/50'
                          }`}
                        >
                          <div className="text-lg font-semibold mb-1">Paiement en 3x</div>
                          <div className="text-sm text-gray-600">{formatPrice(klarnaInstallmentAmount)} /mois</div>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-8">
                      <div className="p-4 rounded-xl border-2 border-neon-orange bg-neon-orange/5">
                        <div className="text-lg font-semibold mb-1">Paiement unique</div>
                        <div className="text-sm text-gray-600">{formatPrice(totalPrice)}</div>
                        {exceedsKlarnaLimit && (
                          <p className="mt-3 text-sm text-amber-700">
                            Klarna n est pas propose au-dela de {currencyService.getKlarnaMaxAmountEUR()} EUR.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Guarantees */}
                  <div className="space-y-6 mb-8">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Type : {getBillingTypeText(service.billingType)}</span>
                    </div>
                    {service.options && service.options.length > 0 && (
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span>{usesSingleChoiceOptions ? 'Packs budget disponibles' : getServiceOptionsSummary(service)}</span>
                        {selectedOptions.length > 0 && (
                          <span className="text-neon-orange">
                            ({usesSingleChoiceOptions ? 'pack choisi' : `${selectedOptions.length} ajoutee${selectedOptions.length > 1 ? 's' : ''}`})
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Délai : {service.timeline}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Satisfaction garantie</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span>Paiement 100% sécurisé</span>
                    </div>
                  </div>

                  {/* Purchase Button */}
                  <button
                    onClick={handlePurchase}
                    className="w-full bg-gradient-to-r from-neon-orange to-[#FF4500] text-white py-4 rounded-full font-semibold relative overflow-hidden group"
                  >
                    <span className="relative z-10">
                      {isStartingPrice ? 'Configurer puis commander' : 'Acheter maintenant'}
                    </span>
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: "100%" }}
                      transition={{ duration: 1 }}
                    />
                  </button>

                  <div className="mt-8 text-center text-sm text-gray-500">
                    En achetant ce service, vous acceptez nos{' '}
                    <Link to="/conditions-generales" className="text-neon-orange hover:underline">
                      conditions générales de vente
                    </Link>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

const tabs = [
  { id: 'description', label: 'Description' },
  { id: 'features', label: 'Fonctionnalités' },
  { id: 'technical', label: 'Technique' },
  { id: 'benefits', label: 'Bénéfices' }
];

export default Purchase;
